import { aesGcmEncrypt, toBase64, fromBase64, aesGcmDecrypt } from "./cryptoHelper";
import { deriveMessageKey, signBytes, verifySignature } from "./cryptoEngine";

/**
 * Encrypted YJS Update structure (matches backend DTO)
 */
export interface EncryptedYjsUpdate {
  sessionId: string;
  filePath: string;
  userId: string;
  username: string;
  messageIndex: number;
  senderKeyId: string;
  nonce: string; // base64
  ciphertext: string; // base64
  signature: string; // base64
  timestamp: string; // ISO datetime
}

/**
 * Local sender key state per document
 */
export interface SenderKeyState {
  sessionId: string;
  filePath: string;
  userId: string;
  senderKeyId: string;
  chainKey: Uint8Array; // Current chain key
  nextMessageIndex: number;
  ed25519PrivateKey: Uint8Array; // For signing
}

/**
 * Remote sender key state (for decryption)
 */
export interface RemoteSenderKeyState {
  sessionId: string;
  filePath: string;
  senderUserId: string;
  senderKeyId: string;
  chainKey: Uint8Array; // Alice's chain key (ratchets with each message)
  rootChainKey?: Uint8Array; // ✨ Original chain key for snapshots (never ratchets)
  lastMessageIndex: number;
  ed25519PublicKey: Uint8Array; // For signature verification
}

/**
 * Encrypts and signs a YJS binary update
 * Based on logic document Step 7: Alice sends "Hello, Bob!"
 * 
 * @param yjsUpdate - Raw YJS binary update (Uint8Array from Y.Doc)
 * @param localSenderKey - Local sender key state for this document
 * @returns EncryptedYjsUpdate ready to send via SignalR
 */
export async function encryptAndSignYjsUpdate(
  yjsUpdate: Uint8Array,
  localSenderKey: SenderKeyState,
  username: string
): Promise<EncryptedYjsUpdate> {

  // derive key for CURRENT index
  const msgIndex = localSenderKey.nextMessageIndex;

  // 7.2 - Derive message key
  const { messageKey, nextChainKey } = await deriveMessageKey(
    localSenderKey.chainKey,
    msgIndex
  );

  // Debug: log sender key state and derived message key snippet
  try {
    console.log('🧾 [SEND-DBG] Preparing encrypted update');
    console.log('  - sessionId:', localSenderKey.sessionId);
    console.log('  - filePath:', localSenderKey.filePath);
    console.log('  - userId:', localSenderKey.userId);
    console.log('  - senderKeyId:', localSenderKey.senderKeyId);
    console.log('  - msgIndex:', msgIndex);
    console.log('  - chainKey snippet:', toBase64(localSenderKey.chainKey).substring(0,16) + '...');
    console.log('  - derived msgKey snippet:', toBase64(messageKey).substring(0,16) + '...');
  } catch (e) {
    console.log('🧾 [SEND-DBG] Error while logging send debug info', e);
  }

  // 7.3 - Encrypt YJS update with AAD
  const encoder = new TextEncoder();
  const sessionIdBytes = encoder.encode(localSenderKey.sessionId);
  const filePathBytes    = encoder.encode(localSenderKey.filePath);      // ✅ add
  const userIdBytes = encoder.encode(localSenderKey.userId);
  const senderKeyIdBytes = encoder.encode(localSenderKey.senderKeyId);
  const messageIndexBytes = u32le(msgIndex);  // ✅ uint32 LE

  const aad = new Uint8Array(
    sessionIdBytes.length +
    filePathBytes.length +     // ✅ add
    userIdBytes.length +
    senderKeyIdBytes.length +
    messageIndexBytes.length
  );

  let offset = 0;
  aad.set(sessionIdBytes, offset);
  offset += sessionIdBytes.length;
  aad.set(filePathBytes,  offset); // ✅ add
  offset += filePathBytes.length;  // ✅ add
  aad.set(userIdBytes, offset);
  offset += userIdBytes.length;
  aad.set(senderKeyIdBytes, offset);
  offset += senderKeyIdBytes.length;
  aad.set(messageIndexBytes, offset);

  const { nonce, ciphertext } = await aesGcmEncrypt(messageKey, yjsUpdate, aad);

  // 7.4 - Sign the message
  const signData = new Uint8Array(
    sessionIdBytes.length +
    filePathBytes.length +     // ✅ add
    userIdBytes.length +
    senderKeyIdBytes.length +
    messageIndexBytes.length +
    nonce.length +
    ciphertext.length
  );

  offset = 0;
  signData.set(sessionIdBytes, offset);
  offset += sessionIdBytes.length;
  signData.set(filePathBytes,  offset); // ✅ add
  offset += filePathBytes.length; // ✅ add
  signData.set(userIdBytes, offset);
  offset += userIdBytes.length;
  signData.set(senderKeyIdBytes, offset);
  offset += senderKeyIdBytes.length;
  signData.set(messageIndexBytes, offset);
  offset += messageIndexBytes.length;
  signData.set(nonce, offset);
  offset += nonce.length;
  signData.set(ciphertext, offset);

  const signature = await signBytes(localSenderKey.ed25519PrivateKey, signData);

  // advance local chain ONLY after encrypt+sign is produced
  localSenderKey.chainKey = nextChainKey;
  localSenderKey.nextMessageIndex = msgIndex + 1;

  // Debug: post-encrypt summary
  try {
    console.log('🧾 [SEND-DBG] Encrypted update prepared');
    console.log('  - sent messageIndex:', msgIndex);
    console.log('  - new nextMessageIndex:', localSenderKey.nextMessageIndex);
    console.log('  - nonce (prefix):', toBase64(nonce).substring(0,16) + '...');
    console.log('  - ciphertext length:', ciphertext.length);
    console.log('  - signature (prefix):', toBase64(signature).substring(0,16) + '...');
    console.log('  - nextChainKey snippet:', toBase64(nextChainKey).substring(0,16) + '...');
  } catch (e) {
    console.log('🧾 [SEND-DBG] Error while logging post-encrypt info', e);
  }

  // 7.5 - Return encrypted update
  const encryptedUpdate = {
    sessionId: localSenderKey.sessionId,
    filePath: localSenderKey.filePath,
    userId: localSenderKey.userId,
    username,
    messageIndex: msgIndex, // The index we just used
    senderKeyId: localSenderKey.senderKeyId,
    nonce: toBase64(nonce),
    ciphertext: toBase64(ciphertext),
    signature: toBase64(signature),
    timestamp: new Date().toISOString(),
  };


  return encryptedUpdate;
}


function u32le(n: number): Uint8Array {
  const b = new Uint8Array(4);
  new DataView(b.buffer).setUint32(0, n >>> 0, true);
  return b;
}
/**
 * Verifies and decrypts an encrypted YJS update
 * Based on logic document Step 8: Bob receives "Hello, Bob!"
 * 
 * @param wire - Encrypted update received via SignalR
 * @param remoteSenderKey - Remote sender key state for the sender
 * @returns Decrypted YJS binary update (Uint8Array for Y.applyUpdate)
 */
export async function verifyAndDecryptYjsUpdate(
  wire: EncryptedYjsUpdate,
  remoteSenderKey: RemoteSenderKeyState
): Promise<Uint8Array> {
  // 8.3 - Verify signature
  const encoder = new TextEncoder();
  const sessionIdBytes = encoder.encode(wire.sessionId);
  const filePathBytes = encoder.encode(wire.filePath);      // add
  const userIdBytes = encoder.encode(wire.userId);
  const senderKeyIdBytes = encoder.encode(wire.senderKeyId);
  const messageIndexBytes = u32le(wire.messageIndex); // 4 bytes little-endian
  const nonceBytes = fromBase64(wire.nonce);
  const ciphertextBytes = fromBase64(wire.ciphertext);
   

  const signData = new Uint8Array(
    sessionIdBytes.length +
    filePathBytes.length +    // add
    userIdBytes.length +
    senderKeyIdBytes.length +
    messageIndexBytes.length +
    nonceBytes.length +
    ciphertextBytes.length
  );

  let offset = 0;
  signData.set(sessionIdBytes, offset);
  offset += sessionIdBytes.length;
  signData.set(filePathBytes,  offset); // ✅ missing before
  offset += filePathBytes.length;   // ✅ missing before
  signData.set(userIdBytes, offset);
  offset += userIdBytes.length;
  signData.set(senderKeyIdBytes, offset);
  offset += senderKeyIdBytes.length;
  signData.set(messageIndexBytes, offset);
  offset += messageIndexBytes.length;
  signData.set(nonceBytes, offset);
  offset += nonceBytes.length;
  signData.set(ciphertextBytes, offset);

  const isValid = await verifySignature(
    remoteSenderKey.ed25519PublicKey,
    signData,
    fromBase64(wire.signature)
  );

  if (!isValid) {
    throw new Error("Invalid YJS update signature");
  }

  // 8.4 - Check replay attack
  if (wire.messageIndex <= remoteSenderKey.lastMessageIndex) {
    throw new Error(`Replay attack detected: ${wire.messageIndex} <= ${remoteSenderKey.lastMessageIndex}`);
  }

  // 8.6 - Decrypt with AAD
  const aad = new Uint8Array(
    sessionIdBytes.length +
    filePathBytes.length +    // add
    userIdBytes.length +
    senderKeyIdBytes.length +
    messageIndexBytes.length
  );

  offset = 0;
  aad.set(sessionIdBytes, offset);
  offset += sessionIdBytes.length;
  aad.set(filePathBytes,  offset); // ✅ missing before
  offset += filePathBytes.length; // ✅ missing before
  aad.set(userIdBytes, offset);
  offset += userIdBytes.length;
  aad.set(senderKeyIdBytes, offset);
  offset += senderKeyIdBytes.length;
  aad.set(messageIndexBytes, offset);

  // (Moved here for clarity)
  // 8.5 - Derive message key
  const { messageKey, nextChainKey } = await deriveMessageKey(
    remoteSenderKey.chainKey,
    wire.messageIndex
  );

  const decryptedUpdate = await aesGcmDecrypt(
    messageKey,
    nonceBytes,
    ciphertextBytes,
    aad
  );

  // 8.7 - Update remote chain (caller should persist this!)
  remoteSenderKey.chainKey = nextChainKey;
  remoteSenderKey.lastMessageIndex = wire.messageIndex;

  // 🔓 LOG: Decrypted content
  console.log('🔓 [DECRYPT] YJS Update Decrypted:');
  console.log('  📄 File:', wire.filePath);
  console.log('  👤 From User:', wire.username);
  console.log('  🔢 Message Index:', wire.messageIndex);
  console.log('  📦 Encrypted Size:', ciphertextBytes.length, 'bytes');
  console.log('  🔓 Decrypted Size:', decryptedUpdate.length, 'bytes');
  console.log('  ✅ Signature Valid');
  console.log('  🎲 Nonce:', wire.nonce.substring(0, 16) + '...');
  console.log('  🔐 Ciphertext (first 32):', wire.ciphertext.substring(0, 32) + '...');

  return decryptedUpdate;
}

export async function verifyAndDecryptYjsUpdate_NoCommit(
  wire: EncryptedYjsUpdate,
  remoteSenderKey: RemoteSenderKeyState,
  isSnapshot: boolean = false // ✨ Flag to use rootChainKey for snapshots
): Promise<{ update: Uint8Array; nextChainKey: Uint8Array }> {
  console.log('🔍 [DECRYPT-DETAIL] ═══════════ DECRYPT BAŞLADI ═══════════');
  console.log('  📊 Wire bilgileri:');
  console.log('    - sessionId:', wire.sessionId);
  console.log('    - filePath:', wire.filePath);
  console.log('    - userId:', wire.userId);
  console.log('    - senderKeyId:', wire.senderKeyId);
  console.log('    - messageIndex:', wire.messageIndex);
  console.log('    - isSnapshot:', isSnapshot);
  console.log('  📊 Remote key bilgileri:');
  console.log('    - remote userId:', remoteSenderKey.senderUserId);
  console.log('    - remote senderKeyId:', remoteSenderKey.senderKeyId);
  console.log('    - remote lastMessageIndex:', remoteSenderKey.lastMessageIndex);
  console.log('    - remote chainKey var mı:', !!remoteSenderKey.chainKey);
  console.log('    - remote rootChainKey var mı:', !!remoteSenderKey.rootChainKey);
  console.log('    - remote ed25519PublicKey var mı:', !!remoteSenderKey.ed25519PublicKey);
  
  const encoder = new TextEncoder();
  
  try {
    console.log('  🔧 [1/6] Encoding metadata...');
    const sessionIdBytes = encoder.encode(wire.sessionId);
    const filePathBytes  = encoder.encode(wire.filePath);
    const userIdBytes    = encoder.encode(wire.userId);
    const senderKeyIdBytes = encoder.encode(wire.senderKeyId);
    const messageIndexBytes = u32le(wire.messageIndex);
    console.log('    ✅ Metadata encoded');
  } catch (e) {
    console.error('    ❌ HATA: Metadata encoding başarısız:', e);
    throw new Error('[DECRYPT-DETAIL] Metadata encoding hatası: ' + (e instanceof Error ? e.message : String(e)));
  }
  
  const sessionIdBytes = encoder.encode(wire.sessionId);
  const filePathBytes  = encoder.encode(wire.filePath);
  const userIdBytes    = encoder.encode(wire.userId);
  const senderKeyIdBytes = encoder.encode(wire.senderKeyId);
  const messageIndexBytes = u32le(wire.messageIndex);
  
  let nonceBytes: Uint8Array;
  let ciphertextBytes: Uint8Array;
  
  try {
    console.log('  🔧 [2/6] Decoding base64 (nonce & ciphertext)...');
    nonceBytes = fromBase64(wire.nonce);
    ciphertextBytes = fromBase64(wire.ciphertext);
    console.log('    ✅ Base64 decoded | nonce:', nonceBytes.length, 'bytes | ciphertext:', ciphertextBytes.length, 'bytes');
  } catch (e) {
    console.error('    ❌ HATA: Base64 decode başarısız:', e);
    throw new Error('[DECRYPT-DETAIL] Base64 decode hatası: ' + (e instanceof Error ? e.message : String(e)));
  }

  // --- signature data (must match sender) ---
  let signData: Uint8Array;
  try {
    console.log('  🔧 [3/6] Building signature data...');
    signData = new Uint8Array(
      sessionIdBytes.length +
      filePathBytes.length +
      userIdBytes.length +
      senderKeyIdBytes.length +
      messageIndexBytes.length +
      nonceBytes.length +
      ciphertextBytes.length
    );

    let offset = 0;
    signData.set(sessionIdBytes, offset); offset += sessionIdBytes.length;
    signData.set(filePathBytes,  offset); offset += filePathBytes.length;
    signData.set(userIdBytes,    offset); offset += userIdBytes.length;
    signData.set(senderKeyIdBytes, offset); offset += senderKeyIdBytes.length;
    signData.set(messageIndexBytes, offset); offset += messageIndexBytes.length;
    signData.set(nonceBytes, offset); offset += nonceBytes.length;
    signData.set(ciphertextBytes, offset);
    console.log('    ✅ Signature data built | total:', signData.length, 'bytes');
  } catch (e) {
    console.error('    ❌ HATA: Signature data build başarısız:', e);
    throw new Error('[DECRYPT-DETAIL] Signature data build hatası: ' + (e instanceof Error ? e.message : String(e)));
  }

  let isValid: boolean;
  try {
    console.log('  🔧 [4/6] Verifying signature...');
    console.log('    - Remote ed25519 public key length:', remoteSenderKey.ed25519PublicKey?.length);
    console.log('    - Wire signature length:', wire.signature?.length);
    isValid = await verifySignature(
      remoteSenderKey.ed25519PublicKey,
      signData,
      fromBase64(wire.signature)
    );
    console.log('    ✅ Signature verification:', isValid ? 'GEÇERLİ ✓' : 'GEÇERSİZ ✗');
  } catch (e) {
    console.error('    ❌ HATA: Signature verification başarısız:', e);
    throw new Error('[DECRYPT-DETAIL] Signature verification hatası: ' + (e instanceof Error ? e.message : String(e)));
  }

  if (!isValid) {
    console.error('    ❌ HATA: Signature GEÇERSİZ!');
    console.error('    → Bu, gönderen kullanıcının public key\' inin yanlış olduğu anlamına gelir');
    console.error('    → Veya mesaj tampered (değiştirilmiş) olabilir');
    throw new Error("[DECRYPT-DETAIL] Invalid YJS update signature");
  }

  // --- AAD (must match sender) ---
  let aad: Uint8Array;
  try {
    console.log('  🔧 [5/6] Building AAD (Additional Authenticated Data)...');
    aad = new Uint8Array(
      sessionIdBytes.length +
      filePathBytes.length +
      userIdBytes.length +
      senderKeyIdBytes.length +
      messageIndexBytes.length
    );

    let offset = 0;
    aad.set(sessionIdBytes, offset); offset += sessionIdBytes.length;
    aad.set(filePathBytes,  offset); offset += filePathBytes.length;
    aad.set(userIdBytes,    offset); offset += userIdBytes.length;
    aad.set(senderKeyIdBytes, offset); offset += senderKeyIdBytes.length;
    aad.set(messageIndexBytes, offset);
    console.log('    ✅ AAD built | total:', aad.length, 'bytes');
  } catch (e) {
    console.error('    ❌ HATA: AAD build başarısız:', e);
    throw new Error('[DECRYPT-DETAIL] AAD build hatası: ' + (e instanceof Error ? e.message : String(e)));
  }

  let messageKey: Uint8Array;
  let nextChainKey: Uint8Array;
  try {
    console.log('  🔧 [5.5/6] Deriving message key from chain key...');
    
    // ✨ CRITICAL FIX: Use rootChainKey for snapshots
    const chainKeyToUse = isSnapshot && remoteSenderKey.rootChainKey 
      ? remoteSenderKey.rootChainKey 
      : remoteSenderKey.chainKey;
    
    if (isSnapshot && remoteSenderKey.rootChainKey) {
      console.log('    🎯 SNAPSHOT MODE: Using rootChainKey instead of ratcheted chainKey');
    }
    
    console.log('    - ChainKey to use:', isSnapshot && remoteSenderKey.rootChainKey ? 'rootChainKey' : 'chainKey');
    console.log('    - ChainKey length:', chainKeyToUse?.length);
    console.log('    - Message index:', wire.messageIndex);
    const derived = await deriveMessageKey(
      chainKeyToUse,
      wire.messageIndex
    );
    messageKey = derived.messageKey;
    nextChainKey = derived.nextChainKey;
    console.log('    ✅ Message key derived');
    console.log('      - messageKey snippet:', toBase64(messageKey).substring(0,16) + '...');
    console.log('      - nextChainKey snippet:', toBase64(nextChainKey).substring(0,16) + '...');
  } catch (e) {
    console.error('    ❌ HATA: Message key derivation başarısız:', e);
    console.error('    → Bu genellikle remote chainKey\' in yanlış olduğu anlamına gelir');
    console.error('    → Remote chainKey:', remoteSenderKey.chainKey);
    console.error('    → Remote rootChainKey:', remoteSenderKey.rootChainKey);
    console.error('    → isSnapshot:', isSnapshot);
    throw new Error('[DECRYPT-DETAIL] Message key derivation hatası: ' + (e instanceof Error ? e.message : String(e)));
  }

  let decryptedUpdate: Uint8Array;
  try {
    console.log('  🔧 [6/6] AES-GCM decryption...');
    console.log('    - Message key length:', messageKey.length);
    console.log('    - Nonce length:', nonceBytes.length);
    console.log('    - Ciphertext length:', ciphertextBytes.length);
    console.log('    - AAD length:', aad.length);
    decryptedUpdate = await aesGcmDecrypt(
      messageKey,
      nonceBytes,
      ciphertextBytes,
      aad
    );
    console.log('    ✅ AES-GCM decryption BAŞARILI!');
    console.log('    - Decrypted update size:', decryptedUpdate.length, 'bytes');
  } catch (e) {
    console.error('    ❌ HATA: AES-GCM decryption başarısız:', e);
    console.error('    → Bu genellikle şu sebeplerden kaynaklanır:');
    console.error('      1. Yanlış message key (chainKey senkronizasyon hatası)');
    console.error('      2. Yanlış nonce');
    console.error('      3. AAD mismatch (metadata farklı)');
    console.error('      4. Ciphertext bozuk veya değiştirilmiş');
    throw new Error('[DECRYPT-DETAIL] AES-GCM decrypt hatası: ' + (e instanceof Error ? e.message : String(e)));
  }

  console.log('🔍 [DECRYPT-DETAIL] ✅✅✅ DECRYPT BAŞARIYLA TAMAMLANDI ✅✅✅');
  return { update: decryptedUpdate, nextChainKey };
}

